/**
 * ModifierGroupsDialog - Neues Dialog-System für Modifier-Groups
 * 
 * Features:
 * - Schritt-für-Schritt Dialoge
 * - Single-Select und Multi-Select
 * - Bedingte Anzeige (z.B. nur bei "Als Menü")
 * - Preis-Kalkulation in Echtzeit
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';

export default function ModifierGroupsDialog({
  product,
  isOpen,
  onClose,
  onConfirm
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen && product) {
      setCurrentStep(0);
      setSelections({});
      setTotalPrice(product?.price || 0);
    }
  }, [isOpen, product]);

  // Get modifier groups safely
  const modifierGroups = useMemo(() => {
    return product?.modifier_groups || [];
  }, [product]);

  // Filter visible groups based on previous selections
  const visibleGroups = useMemo(() => {
    return modifierGroups.filter(group => {
      if (group.show_condition) {
        const conditionMet = Object.values(selections).some(sel => {
          if (Array.isArray(sel)) {
            return sel.some(s => s.name === group.show_condition);
          }
          return sel?.name === group.show_condition;
        });
        return conditionMet;
      }
      return true;
    });
  }, [modifierGroups, selections]);

  // Get current group
  const currentGroup = visibleGroups[currentStep] || null;

  // Calculate total price whenever selections change
  useEffect(() => {
    if (!product) return;
    
    let price = product.price || 0;
    
    Object.values(selections).forEach(sel => {
      if (Array.isArray(sel)) {
        sel.forEach(mod => {
          price += mod.price || 0;
        });
      } else if (sel) {
        price += sel.price || 0;
      }
    });
    
    setTotalPrice(price);
  }, [selections, product]);

  // Handle final confirmation
  const handleFinalConfirm = useCallback(() => {
    const lines = [];
    visibleGroups.forEach(group => {
      const sel = selections[group.modifier_group_id];
      if (sel) {
        if (Array.isArray(sel)) {
          sel.forEach(mod => lines.push(mod.name));
        } else {
          lines.push(sel.name);
        }
      }
    });
    
    onConfirm?.({
      product,
      selections,
      totalPrice,
      modifiersText: lines.join('\n')
    });
    onClose();
  }, [visibleGroups, selections, product, totalPrice, onConfirm, onClose]);

  // Auto-confirm when no more groups
  useEffect(() => {
    if (!currentGroup && currentStep > 0 && isOpen && visibleGroups.length > 0) {
      handleFinalConfirm();
    }
  }, [currentGroup, currentStep, isOpen, visibleGroups.length, handleFinalConfirm]);

  // Early return AFTER all hooks
  if (!isOpen || !product || !product.modifier_groups) {
    return null;
  }

  if (!currentGroup) {
    // No groups or finished all steps
    if (visibleGroups.length === 0) {
      return null;
    }
    return null; // Will auto-confirm via useEffect
  }

  // Handle modifier selection
  const handleSelect = (modifier) => {
    const groupId = currentGroup.modifier_group_id;
    
    if (currentGroup.selection_type === 'single') {
      // Single select - replace
      setSelections(prev => ({
        ...prev,
        [groupId]: modifier
      }));
    } else {
      // Multi select - toggle
      setSelections(prev => {
        const current = prev[groupId] || [];
        const exists = current.find(m => m.modifier_id === modifier.modifier_id);
        
        if (exists) {
          return {
            ...prev,
            [groupId]: current.filter(m => m.modifier_id !== modifier.modifier_id)
          };
        }
        
        // Check max_selections
        if (currentGroup.max_selections && current.length >= currentGroup.max_selections) {
          return prev;
        }
        
        return {
          ...prev,
          [groupId]: [...current, modifier]
        };
      });
    }
  };

  // Calculate total price
  // REMOVED - moved to useEffect above to fix hooks order
  
  // Check if can proceed
  const canProceed = () => {
    if (!currentGroup.required) return true;
    
    const groupSelection = selections[currentGroup.modifier_group_id];
    
    if (currentGroup.selection_type === 'single') {
      return groupSelection != null;
    } else {
      const count = groupSelection?.length || 0;
      return count >= (currentGroup.min_selections || 0);
    }
  };

  // Next step
  const handleNext = () => {
    if (canProceed()) {
      if (currentStep < visibleGroups.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleFinalConfirm();
      }
    }
  };

  // Previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Build modifiers text for display
  const buildModifiersText = () => {
    const lines = [];
    
    visibleGroups.forEach(group => {
      const sel = selections[group.modifier_group_id];
      if (sel) {
        if (Array.isArray(sel)) {
          sel.forEach(mod => {
            lines.push(mod.name);
          });
        } else {
          lines.push(sel.name);
        }
      }
    });
    
    return lines.join('\n');
  };

  // Check if modifier is selected
  const isSelected = (modifier) => {
    const groupSelection = selections[currentGroup.modifier_group_id];
    
    if (currentGroup.selection_type === 'single') {
      return groupSelection?.modifier_id === modifier.modifier_id;
    } else {
      return groupSelection?.some(m => m.modifier_id === modifier.modifier_id) || false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] bg-[#0E1628] border-[#1E293B] shadow-2xl">
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500" />
        
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {product.name}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-teal-400 border-teal-400/50 bg-teal-400/5">
              Schritt {currentStep + 1} von {visibleGroups.length}
            </Badge>
            {currentGroup.required && (
              <Badge variant="outline" className="text-amber-400 border-amber-400/50 bg-amber-400/5">
                Pflichtauswahl
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Group */}
          <div className="bg-[#111C32]/50 rounded-xl p-4 border border-slate-700/50">
            <h3 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {currentGroup.display_name || currentGroup.name}
            </h3>
            {currentGroup.help_text && (
              <p className="text-sm text-slate-400 mt-2">
                {currentGroup.help_text}
              </p>
            )}
          </div>

          {/* Modifiers - Touch-optimized */}
          <div className="grid grid-cols-1 gap-3 max-h-[450px] overflow-y-auto pr-2">
            {currentGroup.modifiers?.map((modifier) => {
              const selected = isSelected(modifier);
              
              return (
                <button
                  key={modifier.modifier_id}
                  onClick={() => handleSelect(modifier)}
                  className={`
                    group relative p-5 rounded-xl border-2 text-left transition-all duration-200
                    min-h-[64px] active:scale-[0.98]
                    ${selected 
                      ? 'border-teal-500 bg-teal-500/15 shadow-lg shadow-teal-500/10' 
                      : 'border-slate-700/60 bg-[#1A2642]/60 hover:border-teal-400/40 hover:bg-[#1E2D4A] hover:-translate-y-0.5 hover:shadow-md'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Checkbox/Radio indicator - Larger for touch */}
                      <div className={`
                        w-7 h-7 rounded ${currentGroup.selection_type === 'single' ? 'rounded-full' : 'rounded-md'} 
                        border-2 flex items-center justify-center transition-all
                        ${selected 
                          ? 'border-teal-500 bg-teal-500 shadow-md shadow-teal-500/30' 
                          : 'border-slate-600 group-hover:border-teal-400/50'
                        }
                      `}>
                        {selected && <Check className="w-4 h-4 text-white stroke-[3]" />}
                      </div>
                      
                      <div>
                        <div className="text-white font-semibold text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {modifier.name}
                        </div>
                      </div>
                    </div>
                    
                    {/* Price - Larger and more prominent */}
                    {modifier.price > 0 && (
                      <div className="text-teal-400 font-bold text-lg" style={{ fontFamily: "'Fira Code', monospace" }}>
                        +{modifier.price.toFixed(2)}€
                      </div>
                    )}
                    {modifier.price === 0 && selected && (
                      <div className="text-emerald-400 text-sm font-medium">
                        Inklusive
                      </div>
                    )}
                  </div>
                  
                  {/* Hover glow effect */}
                  {selected && (
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-teal-500/5 to-emerald-500/5 pointer-events-none" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center mt-6 pt-6 border-t border-slate-700/50">
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="border-slate-600 text-white hover:bg-slate-800 hover:border-slate-500 min-h-[48px] px-6"
              >
                Zurück
              </Button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Total Price - More prominent */}
            <div className="text-right bg-[#111C32]/50 rounded-lg px-4 py-2 border border-slate-700/50">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Gesamt</div>
              <div className="text-3xl font-bold text-teal-400 leading-none mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {totalPrice.toFixed(2)}€
              </div>
            </div>
            
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-teal-500/20 min-h-[56px] px-8 font-semibold text-base transition-all hover:shadow-xl hover:shadow-teal-500/30 active:scale-[0.98]"
            >
              {currentStep < visibleGroups.length - 1 ? 'Weiter' : 'Zum Warenkorb'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
